import React, { useEffect } from 'react';
import { node, object, string } from 'prop-types';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import assetResolver from 'mosaic-assets-resolver';
import { useMount, useUpdate } from '../hooks';
import SovosTheme from '../themes/sovos-theme';
import SovosCustomThemeProvider from '../sovos-custom-theme-context';
import i18n from '../internals/i18n/i18n';
import MergedThemeProvider from '../internals/components/merged-theme-provider/MergedThemeProvider';

/**
 * Every Mosaic application must be wrapped in a SovosProvider component.
 * The provider creates the Mosaic theme for Material UI, loads the default
 * Mosaic font, provides a CSS baseline, and sets up the context for date
 * picker components.
 */
const SovosProvider = _ref => {
  let {
    assetHost,
    children,
    lng = 'en',
    theme = {}
  } = _ref;
  useUpdate(() => {
    // Set assets location
    assetResolver.configure(assetHost);
  }, [assetHost]);
  useMount(async () => {
    // set attribute for loaded fonts
    if (document.fonts) {
      document.fonts.onloadingdone = () => {
        document.documentElement.setAttribute('data-fontsloaded', '');
      };
    }

    // load Inter, Material Icons, and Montserrat
    const fontLink = document.createElement('link');
    fontLink.setAttribute('rel', 'stylesheet');
    fontLink.setAttribute('href', 'https://fonts.googleapis.com/css?family=Inter:300,400,500,700%7CMaterial+Icons%7CMontserrat:700&display=swap');
    document.head.append(fontLink);

    // Set defaults
    const typography = theme.typography || SovosTheme.typography;
    document.body.style.fontFamily = typography.fontFamily;
    document.body.style.margin = 0;
    document.body.style.padding = 0;
  });
  useEffect(() => {
    const currentLng = i18n.language;
    if (currentLng !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [lng]);
  useEffect(() => {
    // eslint-disable-next-line
    const version = "14.2.1";
    if (version) {
      window.mosaicVersion = version;
    }
  }, []);
  return /*#__PURE__*/React.createElement(SovosCustomThemeProvider, null, /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: AdapterDateFns
  }, /*#__PURE__*/React.createElement(MergedThemeProvider, {
    theme: theme
  }, children)));
};
SovosProvider.propTypes = {
  /**
   * Url string for assets host
   */
  assetHost: string,
  /**
   * The content of the provider
   */
  children: node.isRequired,
  /**
   * The language used to localize component labels
   */
  lng: string,
  /**
   * Object for theme overrides
   */
  theme: object
};
export default SovosProvider;